import {
  type InfiniteData,
  type Query,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { ConnectorFeature } from '@/types/core/connector';
import type { Page } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

/** Poll cadence (ms) used while the features read model is still empty. */
const EMPTY_POLL_INTERVAL_MS = 3000;
/**
 * Max successful fetches to make while the list stays empty before giving up.
 * Bounds polling so a genuinely-empty connector — or many dashboard rows — never
 * polls forever. ~10 attempts ≈ 30s of settling time after a connector is created.
 */
const MAX_EMPTY_POLLS = 10;

/**
 * Default poll behavior for the features list. The read model is eventually
 * consistent and can briefly return an empty list right after a connector is
 * created, so we keep polling until features arrive (then stop), bounded by
 * MAX_EMPTY_POLLS so an empty connector settles instead of polling indefinitely.
 */
function pollWhileEmpty(
  query: Query<
    Page<ConnectorFeature>,
    Error,
    InfiniteData<Page<ConnectorFeature>, unknown>
  >
): number | false {
  const hasItems = (query.state.data?.pages?.[0]?.items?.length ?? 0) > 0;
  if (hasItems) return false;
  return query.state.dataUpdateCount < MAX_EMPTY_POLLS
    ? EMPTY_POLL_INTERVAL_MS
    : false;
}

/**
 * List connector features for a given connector.
 */
export function useListConnectorFeatures(
  tenantId: UUID | undefined,
  connectorId: UUID | undefined,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
    enabled?: boolean;
    /**
     * Override the default eventual-consistency poll. Pass `false` to disable
     * polling, or a custom interval/function to change the cadence.
     */
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<
            Page<ConnectorFeature>,
            Error,
            InfiniteData<Page<ConnectorFeature>, unknown>
          >
        ) => number | false | undefined);
  }
): UseInfiniteQueryResult<
  InfiniteData<Page<ConnectorFeature>, unknown>,
  Error
> {
  return useInfiniteQuery<
    Page<ConnectorFeature>,
    Error,
    InfiniteData<Page<ConnectorFeature>, unknown>
  >({
    queryKey: connectorQueryKeys.featureList(
      tenantId,
      connectorId,
      opts?.limit,
      opts?.filter
    ),
    queryFn: (context: QueryFunctionContext) => {
      if (!tenantId || !connectorId) {
        throw new Error('Missing tenantId or connectorId');
      }
      const pageParam = context.pageParam as string | undefined;
      return connectorService.listConnectorFeatures(tenantId, connectorId, {
        limit: opts?.limit,
        next: pageParam,
        filter: opts?.filter,
      });
    },
    enabled: opts?.enabled ?? (!!tenantId && !!connectorId),
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
    refetchInterval: opts?.refetchInterval ?? pollWhileEmpty,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
}
